import React, { useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import TeamGuernsey from '../../SnipeHub/Guernsey';

function num(v) {
    return typeof v === 'number' && !Number.isNaN(v) ? v : null;
}

// Key stats shown in the hover tooltip — mirrors the custom-filter game box.
const TOOLTIP_STATS = [
    { field: 'disposals', label: 'Disp' },
    { field: 'kicks', label: 'Kicks' },
    { field: 'handballs', label: 'HB' },
    { field: 'marks', label: 'Marks' },
    { field: 'tackles', label: 'Tack' },
    { field: 'goals', label: 'Goals' },
    { field: 'clearances', label: 'Clr' },
    { field: 'kick_ins', label: 'KI' },
    { field: 'cba', label: 'CBA', pct: true },
    { field: 'ruck_contests', label: 'RC', pct: true },
    { field: 'tog', label: 'TOG', pct: true },
];

// Heat-map thumbnail with graceful fallback when the image is missing/broken.
function HeatThumb({ src }) {
    const [err, setErr] = useState(false);
    if (src && !err) {
        return (
            <img
                src={src}
                alt="Positional heat map"
                loading="lazy"
                onError={() => setErr(true)}
                className="h-full w-full object-contain"
            />
        );
    }
    return (
        <div className="flex flex-col items-center gap-1 text-slate-700">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M3 12a9 9 0 1018 0 9 9 0 00-18 0zm9-9v18m-9-9h18" />
            </svg>
            <span className="text-[9px] font-mono uppercase tracking-wider">No heat map</span>
        </div>
    );
}

// Picks a clean y-axis ceiling + tick step that hugs the data closely.
// Chooses the smallest "nice" step (1, 2, 2.5, 5 × 10^k) whose ceiling keeps the
// tick count at/under `maxTicks`, so a max of 32 lands on 35 (step 5) rather than
// overshooting to 50.
function niceScale(maxValue, maxTicks = 7) {
    if (!maxValue || maxValue <= 0) return { max: 4, step: 1 };
    const candidates = [];
    for (let p = -2; p <= 4; p++) {
        const mag = Math.pow(10, p);
        [1, 2, 2.5, 5].forEach((m) => candidates.push(m * mag));
    }
    candidates.sort((a, b) => a - b);
    for (const step of candidates) {
        const max = Math.ceil(maxValue / step - 1e-9) * step;
        if (max / step <= maxTicks) return { max, step };
    }
    const step = candidates[candidates.length - 1];
    return { max: Math.ceil(maxValue / step) * step, step };
}

/**
 * Game-by-game bar chart for a single prop stat.
 *  - X axis: round (one bar per game, chronological)
 *  - Y axis: the stat value that game
 * When a betting `line` is supplied, bars are coloured by whether they cleared it
 * (emerald = over/hit, rose = under) and a dashed line marks the threshold.
 * A dashed amber line marks the average across the shown games.
 */
function PropStatBarChart({ games = [], marketLabel = '', line = null, average = null, stackBehinds = false }) {
    const wrapRef = useRef(null);
    const [measuredW, setMeasuredW] = useState(720);
    const [hover, setHover] = useState(null);

    useLayoutEffect(() => {
        const el = wrapRef.current;
        if (!el) return;
        const update = () => setMeasuredW(Math.max(280, el.clientWidth));
        update();
        let ro;
        if (typeof ResizeObserver !== 'undefined') {
            ro = new ResizeObserver(update);
            ro.observe(el);
        } else {
            window.addEventListener('resize', update);
        }
        return () => {
            if (ro) ro.disconnect();
            else window.removeEventListener('resize', update);
        };
    }, []);

    // Games run oldest → newest left-to-right, so open scrolled to the most
    // recent games. Re-runs whenever the dataset (player/market/filters) changes.
    useLayoutEffect(() => {
        const el = wrapRef.current;
        if (el) el.scrollLeft = el.scrollWidth;
    }, [games]);

    const geom = useMemo(() => {
        const padL = 34;
        const padR = 12;
        // Extra top room for the season band that labels each run of games.
        const padT = 30;
        const padB = 58;
        const n = games.length;

        // Group consecutive games by season (games are chronological, so each
        // season is a contiguous run) to drive the season spacers + labels.
        const seasonGroups = [];
        for (let i = 0; i < n; i++) {
            const s = games[i].season;
            const last = seasonGroups[seasonGroups.length - 1];
            if (last && last.season === s) last.end = i;
            else seasonGroups.push({ season: s, start: i, end: i });
        }
        const desiredSlot = 36;
        const contentW = n * desiredSlot;
        const svgW = Math.max(measuredW, padL + padR + contentW);
        const svgH = 260;
        const innerW = svgW - padL - padR;
        const innerH = svgH - padT - padB;
        const slot = n > 0 ? innerW / n : innerW;
        const barW = Math.min(slot * 0.62, 24);

        const maxVal = Math.max(
            1,
            ...games.map((g) => {
                const base = g.value == null ? 0 : g.value;
                return stackBehinds ? base + (g.behinds || 0) : base;
            }),
            line || 0,
            average || 0
        );
        const { max: yMax, step } = niceScale(maxVal);

        const yFor = (v) => padT + innerH - (Math.max(0, v) / yMax) * innerH;
        const xFor = (i) => padL + slot * i + slot / 2;

        const ticks = [];
        const tickCount = Math.round(yMax / step);
        for (let t = 0; t <= tickCount; t++) {
            const v = t * step;
            ticks.push({ v, y: yFor(v) });
        }

        return { padL, padR, padT, padB, svgW, svgH, innerW, innerH, slot, barW, yMax, yFor, xFor, ticks, seasonGroups };
    }, [games, measuredW, line, average, stackBehinds]);

    if (!games.length) return null;

    const { padL, padR, padT, svgW, svgH, slot, barW, yFor, xFor, ticks, seasonGroups } = geom;
    const lineY = line != null ? yFor(line) : null;
    const baselineY = yFor(0);

    // Changing this remounts the bars so the grow-in animation replays — on first
    // load and whenever the stat (marketLabel) or the filtered game set changes.
    const animKey = `${marketLabel}|${games.length}|${games[0]?.key ?? ''}|${games[games.length - 1]?.key ?? ''}`;

    return (
        <div ref={wrapRef} className="relative w-full overflow-x-auto styled-scrollbar">
            <style>{`
                @keyframes propBarGrow { from { transform: scaleY(0.001); } to { transform: scaleY(1); } }
                .prop-bar-grow { transform-box: fill-box; transform-origin: 50% 100%; animation: propBarGrow 0.5s cubic-bezier(0.22, 1, 0.36, 1) backwards; }
            `}</style>
            <svg width={svgW} height={svgH} className="block" role="img" aria-label={`${marketLabel} by round`}>
                <defs>
                    <linearGradient id="propBarHit" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#34d399" />
                        <stop offset="100%" stopColor="#059669" />
                    </linearGradient>
                    <linearGradient id="propBarMiss" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#fb7185" />
                        <stop offset="100%" stopColor="#e11d48" />
                    </linearGradient>
                    <linearGradient id="propBarNeutral" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#38bdf8" />
                        <stop offset="100%" stopColor="#0284c7" />
                    </linearGradient>
                    {/* Hatched silhouette for behinds (scoring-shot overlay). */}
                    <pattern id="propBehinds" patternUnits="userSpaceOnUse" width="5" height="5" patternTransform="rotate(45)">
                        <rect width="5" height="5" fill="rgba(16,185,129,0.10)" />
                        <line x1="0" y1="0" x2="0" y2="5" stroke="rgba(110,231,183,0.75)" strokeWidth="1.6" />
                    </pattern>
                </defs>

                {/* Gridlines + y labels */}
                {ticks.map((t, i) => (
                    <g key={i}>
                        <line x1={padL} y1={t.y} x2={svgW - padR} y2={t.y} stroke="#1e293b" strokeWidth="1" />
                        <text x={padL - 6} y={t.y + 3} textAnchor="end" className="fill-white" style={{ fontSize: 9, fontFamily: 'monospace' }}>
                            {Number.isInteger(t.v) ? t.v : t.v.toFixed(1)}
                        </text>
                    </g>
                ))}

                {/* Season spacers + labels — a divider between each season run and
                    a season label centred over its games. */}
                {seasonGroups.map((grp, gi) => {
                    const count = grp.end - grp.start + 1;
                    const cx = padL + slot * (grp.start + count / 2);
                    const divX = padL + slot * grp.start;
                    return (
                        <g key={`season-${grp.season}-${gi}`}>
                            {gi > 0 && (
                                <line
                                    x1={divX}
                                    y1={padT - 8}
                                    x2={divX}
                                    y2={baselineY}
                                    stroke="#475569"
                                    strokeWidth="1"
                                    strokeDasharray="3 3"
                                    opacity="0.6"
                                />
                            )}
                            <text
                                x={cx}
                                y={13}
                                textAnchor="middle"
                                className="fill-emerald-300/80"
                                style={{ fontSize: 10, fontFamily: 'monospace', fontWeight: 700, letterSpacing: '0.08em' }}
                            >
                                {grp.season}
                            </text>
                        </g>
                    );
                })}

                {/* Line (over/under threshold) */}
                {lineY != null && (
                    <g>
                        <line x1={padL} y1={lineY} x2={svgW - padR} y2={lineY} stroke="#e2e8f0" strokeWidth="1.25" strokeDasharray="2 3" opacity="0.7" />
                        <text x={padL + 2} y={lineY - 4} className="fill-slate-300" style={{ fontSize: 9, fontFamily: 'monospace' }}>
                            line {line}
                        </text>
                    </g>
                )}

                {/* Bars (keyed group so the grow-in animation replays on change) */}
                <g key={animKey}>
                {games.map((g, i) => {
                    const v = g.value == null ? 0 : g.value;
                    const x = xFor(i);
                    const y = yFor(v);
                    const h = Math.max(0, baselineY - y);
                    const isNull = g.value == null;
                    let fill = 'url(#propBarNeutral)';
                    if (line != null && !isNull) fill = v >= line ? 'url(#propBarHit)' : 'url(#propBarMiss)';
                    const dim = hover != null && hover !== i;
                    const growDelay = `${Math.min(i * 14, 500)}ms`;
                    return (
                        <g
                            key={g.key || i}
                            onMouseEnter={() => setHover(i)}
                            onMouseLeave={() => setHover((h2) => (h2 === i ? null : h2))}
                            style={{ cursor: 'default' }}
                        >
                            {/* Hover hit area */}
                            <rect x={x - slot / 2} y={padT} width={slot} height={svgH - padT - geom.padB} fill="transparent" />
                            {isNull ? (
                                <rect x={x - barW / 2} y={baselineY - 3} width={barW} height={3} rx={1.5} fill="#334155" opacity={dim ? 0.4 : 1} />
                            ) : (
                                <rect
                                    x={x - barW / 2}
                                    y={y}
                                    width={barW}
                                    height={h}
                                    rx={3}
                                    fill={fill}
                                    opacity={dim ? 0.45 : 1}
                                    className="prop-bar-grow"
                                    style={{ transition: 'opacity 0.12s', animationDelay: growDelay }}
                                />
                            )}
                            {/* Behinds stacked on top → total scoring shots */}
                            {stackBehinds && !isNull && (g.behinds || 0) > 0 && (() => {
                                const yTop = yFor(v + g.behinds);
                                const bh = Math.max(0, y - yTop);
                                return (
                                    <rect
                                        x={x - barW / 2}
                                        y={yTop}
                                        width={barW}
                                        height={bh}
                                        rx={3}
                                        fill="url(#propBehinds)"
                                        stroke="rgba(110,231,183,0.45)"
                                        strokeWidth="0.75"
                                        opacity={dim ? 0.45 : 1}
                                        className="prop-bar-grow"
                                        style={{ transition: 'opacity 0.12s', animationDelay: growDelay }}
                                    />
                                );
                            })()}
                            {/* Value label — inside the bar when tall enough,
                                otherwise just above it. */}
                            {!isNull && (() => {
                                const inside = h >= 15;
                                return (
                                    <text
                                        x={x}
                                        y={inside ? y + 11 : y - 4}
                                        textAnchor="middle"
                                        className={inside ? 'fill-slate-950' : 'fill-slate-200'}
                                        style={{
                                            fontSize: 9,
                                            fontFamily: 'monospace',
                                            fontWeight: 700,
                                            pointerEvents: 'none',
                                            ...(inside ? { paintOrder: 'stroke', stroke: 'rgba(255,255,255,0.35)', strokeWidth: 2, strokeLinejoin: 'round' } : null),
                                        }}
                                        opacity={dim ? 0.5 : 1}
                                    >
                                        {g.value}
                                    </text>
                                );
                            })()}
                            {/* Round label */}
                            <text
                                x={x}
                                y={svgH - 17}
                                textAnchor="middle"
                                className="fill-white"
                                style={{ fontSize: 9, fontFamily: 'monospace' }}
                            >
                                {g.roundShort}
                            </text>
                            {/* Venue label */}
                            {g.venue ? (
                                <text
                                    x={x}
                                    y={svgH - 5}
                                    textAnchor="middle"
                                    className={hover === i ? 'fill-slate-500' : 'fill-slate-700'}
                                    style={{ fontSize: 8, fontFamily: 'monospace' }}
                                >
                                    {g.venue}
                                </text>
                            ) : null}
                        </g>
                    );
                })}
                </g>

                {/* Baseline */}
                <line x1={padL} y1={baselineY} x2={svgW - padR} y2={baselineY} stroke="#334155" strokeWidth="1.25" />
            </svg>

            {/* Opponent guernseys on the x-axis */}
            {games.map((g, i) => {
                const dim = hover != null && hover !== i;
                return (
                    <div
                        key={`gsy-${g.key || i}`}
                        className="pointer-events-none absolute -translate-x-1/2"
                        style={{ left: xFor(i), top: baselineY + 4, width: 20, opacity: dim ? 0.4 : 1, transition: 'opacity 0.12s' }}
                        title={g.opponent}
                    >
                        <TeamGuernsey name={g.opponentFull || g.opponent} className="w-5 h-5 mx-auto block" />
                    </div>
                );
            })}

            {/* Tooltip — portalled to the body so it can't be clipped by the
                chart's horizontal scroll container, with viewport clamping. */}
            <ChartTooltip
                game={hover != null ? games[hover] : null}
                wrapRef={wrapRef}
                anchorX={hover != null ? xFor(hover) : 0}
                anchorY={hover != null ? yFor((games[hover]?.value == null ? 0 : games[hover].value) + (stackBehinds ? (games[hover]?.behinds || 0) : 0)) : 0}
                marketLabel={marketLabel}
                stackBehinds={stackBehinds}
            />
        </div>
    );
}

// Rich hover tooltip rendered into document.body (fixed positioning) so it is
// never clipped by the scroll container. Shows the same key stats + heat map as
// the custom-filter game box.
function ChartTooltip({ game, wrapRef, anchorX, anchorY, marketLabel, stackBehinds }) {
    if (!game || !wrapRef.current) return null;

    const stat = game.stat || {};
    const r = wrapRef.current.getBoundingClientRect();
    const scrollLeft = wrapRef.current.scrollLeft || 0;
    const screenX = r.left - scrollLeft + anchorX;
    const barTopY = r.top + anchorY;

    const TT_W = 244;
    const margin = 8;
    const half = TT_W / 2;
    const left = Math.max(margin + half, Math.min(screenX, window.innerWidth - margin - half));

    // Flip below the bar when there isn't room above it.
    const estH = 230;
    const placeAbove = barTopY - estH - 12 > margin;
    const top = placeAbove ? barTopY - 12 : barTopY + 12;
    const transform = `translateX(-50%) ${placeAbove ? 'translateY(-100%)' : ''}`;

    const beh = num(stat.behinds) || 0;

    const card = (
        <div
            className="pointer-events-none fixed z-[10001]"
            style={{ left, top, transform, width: TT_W }}
        >
            <div className="rounded-xl bg-slate-900 border border-slate-700/70 shadow-xl shadow-black/60 overflow-hidden">
                {/* Header */}
                <div className="flex items-center gap-2 px-2.5 py-2 border-b border-white/[0.06]">
                    <TeamGuernsey name={game.opponentFull || game.opponent} className="w-5 h-5 flex-shrink-0" />
                    <div className="min-w-0 flex-1">
                        <div className="text-[11px] font-mono font-semibold text-white leading-none truncate">
                            {game.isHome ? 'vs' : '@'} {game.opponent || '—'}
                        </div>
                        <div className="text-[9px] font-mono text-slate-500 mt-0.5 truncate">
                            {game.season} · {game.roundShort}{game.venue ? ` · ${game.venue}` : ''}
                        </div>
                    </div>
                    <div className="text-right flex-shrink-0">
                        <div className="text-[13px] font-mono font-bold text-emerald-300 tabular-nums leading-none">
                            {game.value == null ? 'DNP' : game.value}
                        </div>
                        <div className="text-[8px] font-mono uppercase tracking-wider text-slate-500 mt-0.5 truncate max-w-[64px]">
                            {marketLabel}
                        </div>
                    </div>
                </div>

                {/* Heat map */}
                <div className="relative bg-slate-950/60 h-24 flex items-center justify-center overflow-hidden">
                    <HeatThumb src={stat.heatmap_url} />
                    {stackBehinds && game.value != null && beh > 0 && (
                        <span className="absolute bottom-1 right-1 text-[9px] font-mono text-emerald-300/90 bg-slate-950/70 rounded px-1 py-0.5">
                            +{beh} behinds · {game.value + beh} shots
                        </span>
                    )}
                </div>

                {/* Stats */}
                <div className="grid grid-cols-4 gap-px bg-white/[0.04]">
                    {TOOLTIP_STATS.map(({ field, label, pct }) => {
                        const v = num(stat[field]);
                        return (
                            <div key={field} className="bg-slate-900/80 px-1 py-1.5 text-center">
                                <div className="text-[8px] font-mono uppercase tracking-wider text-slate-600 leading-none">{label}</div>
                                <div className="text-[11px] font-mono font-bold tabular-nums text-slate-200 leading-none mt-1">
                                    {v == null ? '—' : pct ? `${Math.round(v)}%` : v}
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );

    return createPortal(card, document.body);
}

export default PropStatBarChart;
